import { useCallback, useEffect, useState } from "react";
import api, { fmtNZD } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Autocomplete, Typeahead } from "@/components/Autocomplete";
import DeleteProjectDialog from "@/components/DeleteProjectDialog";
import Field from "@/components/Field";
import NumberInput from "@/components/NumberInput";
import DateInput from "@/components/DateInput";
import HazardTemplatePrompt from "@/components/HazardTemplatePrompt";
import { QuickBadge, EstValue, estLabel } from "@/components/QuickBadge";
import { EstimateFields, estBody } from "@/pages/project/EstimatePanel";

const EMPTY_EST = { value: "", low: "", high: "", notes: "", date: new Date().toISOString().slice(0, 10) };
const TYPES = [["full", "Full build (Fixed)"], ["quick", "Quick project (Charge-up)"], ["estimate", "Estimate"]];
const CT = { full: "Fixed Price", quick: "Charge-up", estimate: "Estimate" };

export default function ProjectsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteFor, setDeleteFor] = useState(null);
  const [form, setForm] = useState({
    name: "", reference: "", client_name: "", client_email: "", client_phone: "",
    contract_type: "Fixed Price", address: "", contract_amount: 0,
    start_date: "", completion_date: "", status: "Open", notes: "",
    build_template: "", job_type: "full", description: "",
  });
  const [est, setEst] = useState(EMPTY_EST);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [created, setCreated] = useState(null);
  const [tab, setTab] = useState("Active");
  const [q, setQ] = useState("");
  const tabOf = p => (p.status === "Complete" || p.status === "Archived") ? p.status : "Active";
  const shown = projects.filter(p => tabOf(p) === tab && (!q.trim() ||
    [p.reference, p.name, p.client_name, p.address].some(v => (v || "").toLowerCase().includes(q.trim().toLowerCase()))));
  const afterCreate = useCallback(() => {
    setCreated(c => { if (c) { setOpen(false); nav(`/projects/${c.id}`); } return null; });
  }, [nav]);

  async function loadProjects() {
    try { const { data } = await api.get("/projects"); setProjects(data); } catch {}
  }
  useEffect(() => {
    loadProjects();
    api.get("/build-templates").then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  async function create(e) {
    e.preventDefault(); setErr(""); setMsg("");
    if (!form.name.trim()) { setErr("Project name is required"); return; }
    try {
      const eb = estBody(est);
      const payload = form.job_type === "estimate" ? { ...form, estimate_value: eb.value, estimate_low: eb.low, estimate_high: eb.high, estimate_notes: eb.notes, estimate_date: eb.date } : form;
      const { data } = await api.post("/projects", payload);
      setEst(EMPTY_EST);
      setProjects(ps => [data, ...ps]);
      setMsg(`Project saved ✓ — ${data.name}`);
      setForm({ name:"", reference:"", client_name:"", client_email:"", client_phone:"",
                contract_type:"Fixed Price", address:"", contract_amount:0,
                start_date:"", completion_date:"", status:"Open", notes:"", build_template:"", job_type:"full", description:"" });
      setTimeout(() => setCreated(data), 600);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const detailText = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
        : (detail || e?.message || "Create failed");
      setErr(`Save failed — ${detailText}`);
    }
  }

  return (
    <div className="space-y-4" data-testid="projects-page">
      {created && <HazardTemplatePrompt project={created} onDone={afterCreate}/>}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-2xl">Projects</h1>
        <div className="flex gap-2">
          {user?.role === "Admin" && (
            <button className="btn-ghost text-sm" onClick={() => nav("/projects/deleted")} data-testid="btn-view-deleted">
              Deleted items
            </button>
          )}
          {user?.role !== "Worker" && (
            <button data-testid="btn-new-project" onClick={() => setOpen(true)} className="btn-silver">+ New Project</button>
          )}
        </div>
      </div>

      {open && (
        <form onSubmit={create} className="card" data-testid="new-project-form">
          <h2 className="text-silver mb-3">New project</h2>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="np-type">
            {TYPES.map(([k, l]) => (
              <button type="button" key={k} data-testid={`np-type-${k}`} onClick={() => setForm(f => ({ ...f, job_type: k, contract_type: CT[k] }))}
                className={`px-3 py-1.5 rounded border text-sm ${form.job_type === k ? "bg-silver text-black border-silver" : "border-neutral-700"}`}>{l}</button>
            ))}
            {form.job_type === "quick" && <span className="text-xs opacity-70 self-center">Small time and materials project (charge-up): one 'Job' stage, 3-tick checklist, no quote needed.</span>}
            {form.job_type === "estimate" && <span className="text-xs opacity-70 self-center">Work is recorded like charge-up, with your estimated value to compare against. Not a contract.</span>}
          </div>
          {form.job_type !== "full" ? (<>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Project name" required><input data-testid="np-name" className="input" placeholder="e.g. Smith door lock" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></Field>
              <Field label="Client"><Typeahead kind="client" value={form.client_name} onChange={v=>setForm({...form,client_name:v})} placeholder="Client name" testid="np-client"/></Field>
              <Field label="Site address" className="md:col-span-2"><Autocomplete value={form.address} onChange={v=>setForm({...form,address:v})} placeholder="Site address (start typing)" testid="np-address"/></Field>
              <Field label="Description" className="md:col-span-2"><input data-testid="np-description" className="input" maxLength={300} placeholder="One line, e.g. Replace back door lock" value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></Field>
              <Field label="Start date" hint="DD/MM/YYYY"><DateInput className="input" data-testid="proj-f-start" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})}/></Field>
            </div>
            {form.job_type === "estimate" && <div className="mt-3"><EstimateFields v={est} set={(k, v) => setEst(x => ({ ...x, [k]: v }))} prefix="np-est"/></div>}
            {err && <div className="text-red-500 text-sm mt-2" data-testid="np-error">{err}</div>}
            {msg && <div className="text-green-400 text-sm mt-2" data-testid="np-success">{msg}</div>}
          </>) : (<>
          {err && <div className="text-red-500 text-sm mb-2" data-testid="np-error">{err}</div>}
          {msg && <div className="text-green-400 text-sm mb-2" data-testid="np-success">{msg}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Project name" required>
              <input data-testid="np-name" className="input" placeholder="Project name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
            </Field>
            <Field label="Project number" hint="Optional — your internal reference">
              <input data-testid="np-ref" className="input" placeholder="e.g. 2638" value={form.reference} onChange={e=>setForm({...form,reference:e.target.value})}/>
            </Field>
            <Field label="Client name">
              <Typeahead kind="client" value={form.client_name} onChange={v=>setForm({...form,client_name:v})} placeholder="Client name" testid="np-client"/>
            </Field>
            <Field label="Client email">
              <input className="input" placeholder="name@example.com" value={form.client_email} onChange={e=>setForm({...form,client_email:e.target.value})}/>
            </Field>
            <Field label="Client phone">
              <input className="input" placeholder="e.g. 027 555 1234" value={form.client_phone} onChange={e=>setForm({...form,client_phone:e.target.value})}/>
            </Field>
            <Field label="Contract type">
              <select className="input" value={form.contract_type} data-testid="np-contract-type" onChange={e=>setForm({...form,contract_type:e.target.value, job_type: e.target.value === "Estimate" ? "estimate" : form.job_type})}>
                <option>Fixed Price</option><option>Charge-up</option><option>Cost Plus</option><option>Estimate</option>
              </select>
            </Field>
            <Field label="Site address" className="md:col-span-2" hint="Start typing — NZ address autocomplete">
              <Autocomplete value={form.address} onChange={v=>setForm({...form,address:v})} placeholder="Site address (start typing)" testid="np-address"/>
            </Field>
            <Field label="Contract value (ex GST)" hint="NZ dollars, excluding GST">
              <NumberInput className="input" placeholder="Contract value ex GST ($)" value={form.contract_amount} onChange={e=>setForm({...form,contract_amount:parseFloat(e.target.value||0)})} data-testid="np-contract"/>
            </Field>
            <Field label="Start date" hint="DD/MM/YYYY">
              <DateInput className="input" data-testid="proj-f-start" placeholder="Start date (DD/MM/YYYY)" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})}/>
            </Field>
            <Field label="Completion date" hint="DD/MM/YYYY">
              <DateInput className="input" data-testid="proj-f-complete" placeholder="Completion date (DD/MM/YYYY)" value={form.completion_date} onChange={e=>setForm({...form,completion_date:e.target.value})}/>
            </Field>
            <Field label="Status">
              <select className="input" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                <option>Open</option><option>On Hold</option><option>Closed</option>
              </select>
            </Field>
            <Field label="Build template" className="md:col-span-2" hint="Seeds the project's build stages. Leave blank to start from scratch.">
              <select data-testid="np-template" className="input" value={form.build_template} onChange={e=>setForm({...form,build_template:e.target.value})}>
                <option value="">— Start from blank —</option>
                {templates.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </Field>
            <Field label="Notes" className="md:col-span-2">
              <textarea className="input" rows={3} placeholder="Internal notes (not visible to the client)" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/>
            </Field>
          </div>
          </>)}
          <div className="mt-4 flex gap-2">
            <button className="btn-silver" data-testid="np-create">Create</button>
            <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      )}

      <div className="flex gap-2 flex-wrap items-center" data-testid="jobs-filter">
        {["Active", "Complete", "Archived"].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`jobs-tab-${t.toLowerCase()}`}
            className={`px-3 py-1.5 rounded text-sm ${tab === t ? "bg-silver text-black" : "btn-ghost"}`}>
            {t} ({projects.filter(p => tabOf(p) === t).length})
          </button>
        ))}
        <input className="input flex-1 min-w-[160px] max-w-xs" placeholder="Search projects…" value={q} onChange={e => setQ(e.target.value)} data-testid="jobs-search" />
      </div>

      <div className="card overflow-x-auto">
        <table className="table">
          <thead><tr><th>Ref</th><th>Name</th><th>Client</th><th>Address</th><th>Contract</th><th>Status</th>{user?.role === "Admin" && <th></th>}</tr></thead>
          <tbody>
            {shown.map(p => (
              <tr key={p.id} className="cursor-pointer" data-testid={`project-row-${p.id}`} onClick={() => nav(`/projects/${p.id}`)}>
                <td>{p.reference || "—"}</td>
                <td className="text-silver">{p.name}<QuickBadge p={p}/></td>
                <td>{p.client_name || "—"}</td>
                <td>{p.address || "—"}</td>
                <td>{p.pricing === "estimate" ? <EstValue label={estLabel(p.estimate_info)} testid={`project-est-${p.id}`}/> : fmtNZD(p.contract_amount)}</td>
                <td><span className="badge badge-grey">{p.status}</span></td>
                {user?.role === "Admin" && (
                  <td onClick={e=>e.stopPropagation()}>
                    <button className="text-xs text-red-400" data-testid={`btn-delete-project-${p.id}`}
                            onClick={() => setDeleteFor(p)}>Delete</button>
                  </td>
                )}
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={user?.role === "Admin" ? 7 : 6} className="opacity-60">No {tab.toLowerCase()} projects.</td></tr>}
          </tbody>
        </table>
      </div>

      {deleteFor && (
        <DeleteProjectDialog project={deleteFor}
          onClose={() => setDeleteFor(null)}
          onDeleted={() => { setDeleteFor(null); loadProjects(); setMsg(`Moved to Deleted bin.`); }}/>
      )}
    </div>
  );
}
